import { useCallback, useMemo } from "react";

import { type DocumentNode } from "graphql";

import { useNorthbeamLazyQuery } from "@hooks/http";
import { type GetSalesChartReportVariables } from "@north-beam/nb-client-side";
import {
  type DateInterval,
  type IGranularity,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularity,
} from "@north-beam/nb-common";

export interface SalesReportProps {
  dateRange: DateInterval;
  compareDateRange: DateInterval;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  nbAccountingMode: INBAccountingMode;
  timeGranularity: ITimeGranularity;
}

export enum GRANULARITY {
  CAMPAIGN = "campaign",
  ADSET = "adset",
  AD = "ad",
}

export function useLazyLoadSalesReport<
  Query,
  Variables extends GetSalesChartReportVariables,
>(query: DocumentNode) {
  const {
    loadData,
    data: { data, loading, error, refetch },
  } = useNorthbeamLazyQuery<Query, Variables>(query, {
    useHeavy: true,
    returnPartialData: true,
    doNotRaiseErrorOnFailure: true,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
  });

  return { loadData, data, loading, error, refetch };
}

export function useSalesReport<
  Query,
  Variables extends GetSalesChartReportVariables,
>(granularity: IGranularity, variables: SalesReportProps, query: DocumentNode) {
  const {
    dateRange,
    compareDateRange,
    attributionModel,
    attributionWindow,
    nbAccountingMode,
    timeGranularity,
  } = variables;

  const commonGQLVariables = useMemo(() => {
    return {
      dateRange,
      compareDateRange,
      attributionMethod: attributionModel,
      attributionWindow,

      nbAccountingMode,
      timeGranularity,
    };
  }, [
    attributionModel,
    attributionWindow,
    compareDateRange,
    dateRange,
    nbAccountingMode,
    timeGranularity,
  ]);

  const campaignVariables = useMemo(() => {
    return {
      granularity: GRANULARITY.CAMPAIGN,
      ...commonGQLVariables,
    } as Variables;
  }, [commonGQLVariables]);
  const {
    loadData: loadCampaignData,
    data: campaignData,
    loading: campaignLoading,
    error: campaignError,
    refetch: campaignRefetch,
  } = useLazyLoadSalesReport<Query, Variables>(query);

  const adsetVariables = useMemo(() => {
    return {
      granularity: GRANULARITY.ADSET,
      ...commonGQLVariables,
    } as Variables;
  }, [commonGQLVariables]);
  const {
    loadData: loadAdsetData,
    data: adsetData,
    loading: adsetLoading,
    error: adsetError,
    refetch: adsetRefetch,
  } = useLazyLoadSalesReport<Query, Variables>(query);

  const adVariables = useMemo(() => {
    return {
      granularity: GRANULARITY.AD,
      ...commonGQLVariables,
    } as Variables;
  }, [commonGQLVariables]);
  const {
    loadData: loadAdData,
    data: adData,
    loading: adLoading,
    error: adError,
    refetch: adRefetch,
  } = useLazyLoadSalesReport<Query, Variables>(query);

  const loadAllData = useCallback(
    (additionalVariables?: Partial<Variables>) => {
      loadCampaignData({
        variables: {
          ...campaignVariables,
          ...(additionalVariables && additionalVariables),
        },
      });
      loadAdsetData({
        variables: {
          ...adsetVariables,
          ...(additionalVariables && additionalVariables),
        },
      });
      loadAdData({
        variables: {
          ...adVariables,
          ...(additionalVariables && additionalVariables),
        },
      });
    },
    [
      loadCampaignData,
      campaignVariables,
      loadAdsetData,
      adsetVariables,
      loadAdData,
      adVariables,
    ],
  );

  const { data, isLoading, error, refetch } = useMemo(() => {
    let data = campaignData;
    let isLoading = campaignLoading;
    let error = campaignError;
    let refetch = campaignRefetch;
    if (granularity === GRANULARITY.ADSET) {
      data = adsetData;
      isLoading = adsetLoading;
      error = adsetError;
      refetch = adsetRefetch;
    } else if (granularity === GRANULARITY.AD) {
      data = adData;
      isLoading = adLoading;
      error = adError;
      refetch = adRefetch;
    }

    return {
      data,
      isLoading,
      error,
      refetch,
    };
  }, [
    adData,
    adError,
    adLoading,
    adRefetch,
    adsetData,
    adsetError,
    adsetLoading,
    adsetRefetch,
    campaignData,
    campaignError,
    campaignLoading,
    campaignRefetch,
    granularity,
  ]);

  const loadOnlyAdsetsData = useCallback(
    (additionalVariables?: Partial<Variables>) => {
      loadCampaignData({ variables: { ...campaignVariables, fakeCall: true } });
      loadAdData({ variables: { ...adVariables, fakeCall: true } });
      return loadAdsetData({
        variables: {
          ...adsetVariables,
          ...(additionalVariables && additionalVariables),
        },
      });
    },
    [
      adVariables,
      adsetVariables,
      campaignVariables,
      loadAdData,
      loadAdsetData,
      loadCampaignData,
    ],
  );

  const loadOnlyCampaignData = useCallback(
    (additionalVariables?: Partial<Variables>) => {
      loadAdData({ variables: { ...adVariables, fakeCall: true } });
      loadAdsetData({ variables: { ...adsetVariables, fakeCall: true } });
      return loadCampaignData({
        variables: {
          ...campaignVariables,
          ...(additionalVariables && additionalVariables),
        },
      });
    },
    [
      adVariables,
      adsetVariables,
      campaignVariables,
      loadAdData,
      loadAdsetData,
      loadCampaignData,
    ],
  );

  const loadOnlyAdData = useCallback(
    (additionalVariables?: Partial<Variables>) => {
      loadCampaignData({ variables: { ...campaignVariables, fakeCall: true } });
      loadAdsetData({ variables: { ...adsetVariables, fakeCall: true } });
      return loadAdData({
        variables: {
          ...adVariables,
          ...(additionalVariables && additionalVariables),
        },
      });
    },
    [
      adVariables,
      adsetVariables,
      campaignVariables,
      loadAdData,
      loadAdsetData,
      loadCampaignData,
    ],
  );

  return {
    loadOnlyAdsetsData,
    loadOnlyCampaignData,
    loadOnlyAdData,
    loadAllData,
    data,
    isLoading,
    error,
    refetch,
  };
}
